import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { useState } from 'react'
import culturePlateWorkflowAPI from '~/api/desktop/culturePlateWorkflow'
import workflowInstancesAPI from '~/api/desktop/workflowInstances'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Tabs from '~/components/Tabs'
import WorkflowsInstanceHeader from '~/pages/Workcell/Schedule/Workflows/WorkflowsInstanceHeader'
import PendingApprovalsTable from './PendingApprovalsTable'
import WorkflowInstanceTable from './WorkflowsInstanceTable'
import cs from './workflow_instances.scss'

// Poll for workflow updates every 60 seconds
const WORKFLOWS_POLL_INTERVAL_MS = 60000

const WorkflowInstances = ({ className }: { className?: string }) => {
  const [selectedTab, setSelectedTab] = useState<'active' | 'pending'>('active')

  // Fetch active workflows with React Query
  const {
    data: activeWorkflowsData,
    isLoading: activeLoading,
    refetch: refetchActive,
  } = useQuery({
    queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_ACTIVE],
    queryFn: async () => {
      const response =
        await culturePlateWorkflowAPI.getActiveWorkflowInstancesWithCulturePlates()
      return response.workflow_instances_with_culture_plate
    },
    // Only poll when the active tab is selected
    refetchInterval: selectedTab === 'active' ? WORKFLOWS_POLL_INTERVAL_MS : false,
  })

  // Fetch pending workflows with React Query
  const {
    data: pendingWorkflowsData,
    isLoading: pendingLoading,
    refetch: refetchPending,
  } = useQuery({
    queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_PENDING],
    queryFn: async () => {
      const response = await workflowInstancesAPI.listPending()
      return response.workflow_instances
    },
    // Only poll when the pending tab is selected
    refetchInterval: selectedTab === 'pending' ? WORKFLOWS_POLL_INTERVAL_MS : false,
  })

  // Normalize data to arrays to simplify child components
  const activeWorkflowInstancesWithCulturePlates = activeWorkflowsData ?? []
  const pendingWorkflows = pendingWorkflowsData ?? []

  const refreshWorkflows = () => {
    // Refresh both tabs when a workflow is approved/rejected/updated
    // since workflows can move between tabs or change state
    refetchActive()
    refetchPending()
  }

  const tabs = [
    {
      key: 'active',
      title: 'Active Workflows',
    },
    {
      key: 'pending',
      title: `Pending Approvals${
        pendingWorkflows && pendingWorkflows.length > 0
          ? ` (${pendingWorkflows.length})`
          : ''
      }`,
    },
  ]

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab as 'active' | 'pending')
  }

  return (
    <div className={cx(className, cs.workflowInstances)}>
      <WorkflowsInstanceHeader className={className} />
      <Tabs
        tabs={tabs}
        selectedTabId={selectedTab}
        onTabChange={handleTabChange}
        className={cs.tabs}
        panelClassName={cs.tabPanel}
        nested
        empty
      />
      {selectedTab === 'active' ? (
        <WorkflowInstanceTable
          className={className}
          workflowInstancesWithCulturePlates={activeWorkflowInstancesWithCulturePlates}
          loading={activeLoading}
          onWorkflowUpdate={refreshWorkflows}
        />
      ) : (
        <PendingApprovalsTable
          className={className}
          pendingWorkflows={pendingWorkflows}
          loading={pendingLoading}
          onWorkflowUpdate={refreshWorkflows}
        />
      )}
    </div>
  )
}

export default WorkflowInstances
